import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {JobService} from '../../shared/services/job.service';
import {CompanyService} from '../../shared/services/company.service';
import {ModalService} from '../../shared/services/modal.service';
import {faCalendar, faTrash, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {ModalStudentApplicationMotivationComponent} from '../../student/student-applications/modal-student-application-motivation/modal-student-application-motivation.component';
import {empty} from '../../functions';
import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {catchError} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
import {alertMessages, AlertService} from '../../shared/services/alert.service';

@Component({
    selector: 'app-company-applications',
    templateUrl: './company-applications.component.html',
    styleUrls: ['./company-applications.component.scss'],
    standalone: false
})
export class CompanyApplicationsComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);

  // Icons
  faCalendar = faCalendar;
  faTrash = faTrash;
  faInfoCircle = faInfoCircle;

  public view = {
    isLoading: false,
    page: 1,
    limit: 30,
    total_results: 0,
    total_pages: 1,
    filters: {},
    limitOptions: [
      {
        value: 30,
        label: 30
      },
      {
        value: 60,
        label: 60
      },
      {
        value: 120,
        label: 120
      },
      {
        value: 300,
        label: 300
      }
    ],
    orderByOptions: [
      {
        value: 'asc',
        label: 'Old to new'
      },
      {
        value: 'desc',
        label: 'New to old'
      }
    ],
    data: <any> []
  };

  public profile = {
    id: ''
  };

  selectableProfiles:any[] = [];

  /**
   * Constructor
   */
  constructor(private companyService: CompanyService,
              private jobService: JobService,
              public route: ActivatedRoute,
              public router: Router,
              public modal: ModalService,
              private alertService:AlertService) {


  }

  /**
   * Ng on init
   */
  ngOnInit(): void {

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe( (params:ParamMap) => {

      const id : string = <string> params.get('id');

      // If ID is found, load the profile
      if (!empty(id)) {

        this.loadCompanyprofile(id);

      }
      // Or else load all company profiles related to account
      else {

        // Get current company to show editable profiles
        this.companyService.apiGetCompanyCurrent().pipe(catchError(this.handleError('apiGetCompanyCurrent', false)), takeUntilDestroyed(this.destroyRef)).subscribe((companyProfiles: any) => {

          if (companyProfiles) {

            // If only profile is found, auto redirect to the profile
            if (companyProfiles.length == 0) {

            }

            // If only profile is found, auto redirect to the profile
            if (companyProfiles.length == 1) {
              const companyProfile = companyProfiles[0];
              this.router.navigateByUrl('/company/applications/' + companyProfile['id']);
            }

            // If only profile is found, auto redirect to the profile
            if (companyProfiles.length > 1) {
              this.selectableProfiles = companyProfiles;
            }

          }

        });

      }

    });

  }

  /**
   * Load company profile
   *
   * @param id
   * @param reload
   */
  loadCompanyprofile(id: string, reload = false) {

    if (id) {

      this.companyService.apiGetCompanyProfile(id).pipe(catchError(this.handleError('apiGetCompanyProfile', false)), takeUntilDestroyed(this.destroyRef)).subscribe( (companyProfile:any) => {

        if (companyProfile) {

          this.profile = Object.assign({}, companyProfile);

          // Load company specific job postings along with applications
          this.loadJopPostingApplications();

        } else {

          // Do something if profile is not found

        }

      });

    }
  }

  /**
   * Load job posting application data
   */
  loadJopPostingApplications() {

    if (empty(this.profile.id)) return;

    this.view.isLoading = true;

     const $queryParams = {
      pageSize: this.view.limit,
      pageNumber: this.view.page
    }

    // First get the company specific job postings and then load applications related to the jop posting
    this.jobService.apiGetCompanyJobs(this.profile.id, $queryParams).pipe(catchError(this.handleError('apiGetCompanyJobs', false)), takeUntilDestroyed(this.destroyRef)).subscribe( (jobpostings:any) => {

      this.view.data = [];

      if (jobpostings !== false && !empty(jobpostings.data)) {

        this.view.total_results = jobpostings.totalItems;

       for (const j in jobpostings.data) {

          const jobposting = jobpostings.data[j];

          // Get all applications of the job posting
          this.jobService.apiGetJobPostingApplications(jobposting['id']).pipe(catchError(this.handleError('apiGetJobPostingApplications', false)), takeUntilDestroyed(this.destroyRef)).subscribe( (applications: any) => {

            jobpostings.data[j]['applications'] = [];

            if (applications !== false) {

              jobpostings.data[j]['applications'] = Object.assign([], applications);

              // Reverse order
              jobpostings.data[j]['applications'].reverse();

            }

            this.view.data.push(jobpostings.data[j]);

            if (parseInt(j, 10) == (jobpostings.data.length - 1)) {
              this.view.isLoading = false;
            }

         });

       }

      } else {

        this.view.isLoading = false;
      }

      this.setTotalPages();

    });
  }

  /**
   * Open motivation modal
   *
   * @param motivation
   */
  openMotivationModal(motivation: string) {
    const data = {
      motivation: motivation
    }
    this.modal.Modal(ModalStudentApplicationMotivationComponent, 'Motivation', '', data, []).then( (result) => {

      // No specific action
    });
  }

  /**
   * Set total pages
   */
  setTotalPages() {
    this.view.total_pages = (this.view.total_results > 0) ? Math.ceil(this.view.total_results / this.view.limit) : 1;
  }

  /**
   * Set (current) page
   *
   * To use with pagination
   *
   * @param $value
   */
  setPage($value: any) {
    this.view.page = $value;
    this.loadJopPostingApplications();
  }

  /**
   * Error handler
   *
   * @param operation
   * @param result
   */
  handleError<T>(operation:string = '', result?: T) {
    return (error: any): Observable<T> => {

      // Handle given operation
      switch (operation) {

        case 'apiGetCompanyCurrent':
          // Do not perform any action, profile could be non-existing
          break;

        case 'apiUpdateCompanyprofileContact':
        default:
          this.alertService.errorWithReport(error, `companyApplications:${operation}`);
          break;

      }

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }


}
